import { Input } from '@treasuryspring/spring-ui';
import classNames from 'classnames';
import { toNumber } from 'lodash';
import { Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useRatesQuery } from '@/api/query';
import { useAppStore } from '@/stores/useAppStore';
import {
  amountInWords,
  calculateMaturityValue,
  formatYieldString,
  getCurrencySymbol,
  termInDays,
  verboseTerm,
} from '@/utils/helper-functions';

import CurrencyLabel from './currency-label';
import ValuationTable from './valutation-table';

interface ValuationProps {
  className?: string;
}

function Valuation({ className }: ValuationProps) {
  const { selectedProduct, selectedCurrency } = useAppStore();
  const [amountToInvest, setAmountToInvest] = useState<string>();
  const { yieldConvention } = useRatesQuery().data || {
    yieldConvention: 'APY',
  };

  const maturityValue = useMemo(() => {
    if (!amountToInvest || !selectedProduct) return 0;
    return calculateMaturityValue(
      toNumber(amountToInvest),
      selectedProduct.term,
      selectedProduct.rate,
      yieldConvention
    );
  }, [amountToInvest, selectedProduct, yieldConvention]);

  const maturityIncome = useMemo(
    () => maturityValue - toNumber(amountToInvest),
    [maturityValue, amountToInvest]
  );

  const annualizedIncome = useMemo(() => {
    if (!amountToInvest || !selectedProduct) return 0;
    return (
      calculateMaturityValue(
        toNumber(amountToInvest),
        '12m',
        selectedProduct.rate,
        yieldConvention!
      ) - toNumber(amountToInvest)
    );
  }, [amountToInvest, selectedProduct, yieldConvention]);

  const productTerm = useMemo(() => {
    if (!selectedProduct) return '';
    const termComponents = selectedProduct.term.split(' ');
    return `${verboseTerm(termComponents[0])}${
      termComponents.length > 1 ? ` (${termComponents[1]})` : ''
    }`;
  }, [selectedProduct]);

  const currencySymbol = useMemo(
    () => getCurrencySymbol(selectedCurrency),
    [selectedCurrency]
  );

  useEffect(() => {
    if (!selectedProduct) {
      setAmountToInvest('');
    }
  }, [selectedProduct]);

  return (
    <div className={className}>
      <h3
        className={classNames(
          'sm:text-subtitle1 text-subtitle2 mb-s sm:gap-xs gap-xxs flex flex-nowrap items-center',
          {
            'animate-pulse': !selectedProduct,
          }
        )}
      >
        {!selectedProduct && <Info className="sm:size-l size-m" />}
        {selectedProduct
          ? `Investing for ${productTerm} at ${formatYieldString(
              selectedProduct.rate
            )}%`
          : 'Click on a bar within the chart to see how much you can earn with that product'}
      </h3>
      {selectedProduct && (
        <>
          <div className="relative">
            <div className="px-s bg-background absolute z-10 box-border flex h-full w-fit items-center justify-center rounded-tl-sm rounded-bl-sm border">
              <CurrencyLabel
                currency={selectedCurrency}
                className="text-body-md"
              />
            </div>
            <Input
              type="number"
              placeholder="Amount to invest"
              value={amountToInvest}
              onChange={(e) => setAmountToInvest(e.target.value)}
              disabled={!selectedProduct}
              className="pl-[5.5rem]"
            />
          </div>
          {amountToInvest !== '' && (
            <p className="text-hint text-description mt-xxs">
              {amountInWords(toNumber(amountToInvest))}
            </p>
          )}
          <ValuationTable
            className="mt-s"
            maturityValue={maturityValue}
            annualizedIncome={annualizedIncome}
            maturityIncome={maturityIncome}
            currencySymbol={currencySymbol}
          />
          <p className="text-disclaimer text-description mt-s">
            Note: calculations are based on the assumption that{' '}
            {selectedProduct.term.split(' ')[0]} ={' '}
            {termInDays(selectedProduct.term)} days. The exact term of some
            products may vary.
          </p>
        </>
      )}
    </div>
  );
}

export default Valuation;
